class YGImageLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const picture = img.closest('picture');

            if (picture) {
              picture.querySelectorAll('source[data-srcset]').forEach(source => {
                source.setAttribute('srcset', source.getAttribute('data-srcset'));
                source.removeAttribute('data-srcset');
              });

              if (img.getAttribute('data-src')) {
                img.src = img.getAttribute('data-src');
                img.removeAttribute('data-src');
              }

              observer.unobserve(img);
              return;
            }

            if (img.getAttribute('data-srcset')) {
              img.srcset = img.getAttribute('data-srcset');
              img.removeAttribute('data-srcset');
            }

            if (img.getAttribute('data-src')) {
              img.src = img.getAttribute('data-src');
              img.removeAttribute('data-src');
            }

            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: "50px",
      });
    }
    return this._lazyLoader;
  }
}

class YGVideoLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const video = entry.target;
            const template = video.querySelector('template');
            if (template) {
              video.appendChild(template.content.cloneNode(true));
            }
            observer.unobserve(video);
          }
        });
      }, {
        rootMargin: "50px",
      });
    }
    return this._lazyLoader;
  }
}
class YGUtils extends HTMLElement {
  // 防抖函数 - 延迟执行函数，避免频繁调用
  debounce(func, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  // 节流函数 - 限制函数执行频率
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  get #getCurrentSlidesPerView() {
    if (!this['breakpoints']) return 1;

    const screenWidth = window.innerWidth;

    // 将breakpoints按数值从小到大排序
    const sortedKeys = Object.keys(this['breakpoints']).map(Number).sort((a, b) => a - b);

    let current = sortedKeys[0]; // 默认取最小的

    // 遍历找到小于等于当前屏幕宽度的最大key
    for (let i = 0; i < sortedKeys.length; i++) {
      if (screenWidth >= sortedKeys[i]) {
        current = sortedKeys[i];
      } else {
        break;
      }
    }
  
    return this['breakpoints'][current].slidesPerView;
  }

  #getAllAttributes(dom) {
    let booleanAttrs = ['loop', 'pagination', 'navigation', 'scrollbar', 'centered-slides'];
    let jsonAttrs = ['breakpoints']
    for (const attr of dom.attributes) {
      if (attr.name.startsWith('data-')) {
        const cleanName = attr.name.slice(5); // 去除 'data-' 前缀
        
        let value = attr.value;
        if (booleanAttrs.includes(cleanName)) {
          value = value === 'true';
        } else if (jsonAttrs.includes(cleanName)) {
          value = this.#parseSwiperBreakpoints(value);
        }

        this[cleanName] = value;
      }
    }
  }

  #parseSwiperBreakpoints(str) {
    const result = {};
    const pairs = str.split(',');

    for (const pair of pairs) {
      const [breakpoint, values] = pair.split(':');
      if (!breakpoint || !values) continue;
  
      const [slides, space] = values.split('-').map(Number);
  
      result[breakpoint.trim()] = {
        slidesPerView: slides,
        spaceBetween: space
      };
    }

    return result;
  }

  initSwiper(dom, thumbnails) {
    this.#getAllAttributes(dom || this);
    const swiper = new Swiper(dom || this, {
      // 这里放置 swiper 的配置
      effect: this['effect'],
      loop: this['loop'],
      centeredSlides: this['centered-slides'],
      autoplay: this['autoplay']?{
        delay: this['autoplay'],
        stopOnLastSlide: false,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      }:false,
      pagination: this['pagination']?{
        el: '.swiper-pagination',
        type: this['pagination-type']?this['pagination-type']:'bullets',
        clickable: true,
      }:false,
      navigation: this['navigation']?{
        nextEl: this['navigation-class']? `.${this['navigation-class']}-next`:'.swiper-button-next',
        prevEl: this['navigation-class']? `.${this['navigation-class']}-prev`:'.swiper-button-prev',
      }:false,
      breakpoints: this['breakpoints']?this['breakpoints']:false,
      on: {
        init: e => {
          if (e.passedParams.loop) {
            let num = this.#getCurrentSlidesPerView;
            let sliderLength = e.slides.length;

            if (this['centered-slides']) {
              num += 4;
            }

            if(sliderLength <= num){
              for (let index = 1; index < Math.ceil(num / sliderLength); index++) {
                e.slides.forEach((slide) => {
                  const clonedElement = slide.cloneNode(true);
                  e.slidesEl.appendChild(clonedElement);
                })
              }

              setTimeout(() => {
                let $bullets = this.querySelectorAll('.swiper-pagination-bullet');
                $bullets.forEach((bullet, index) => {
                  if (index >= sliderLength) {
                    bullet.style.display = 'none';
                  }
                })
              }, 250)
            }
          }
        }
      },
      thumbs: {
        swiper: thumbnails
      }
    })

    return swiper;
  }

  getLatestDom(requestUrlOrCallback, callback) {
    // 支持两种调用方式：
    // 1. getLatestDom(callback) -> 使用当前页面 URL
    // 2. getLatestDom(requestUrl, callback)
    let requestUrl = requestUrlOrCallback;
    if (typeof requestUrlOrCallback === 'function') {
      callback = requestUrlOrCallback;
      requestUrl = window.location.href;
    } else {
      requestUrl = requestUrl || window.location.href;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const promise = fetch(requestUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (typeof callback === 'function') {
          callback(html);
        }
        return html;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
        // 将错误继续抛出，便于外部处理
        throw error;
      });

    // 返回 Promise，调用者可以选择使用回调或 await 返回值
    return promise;
  }
}

class YGSwiper extends YGUtils {
  constructor() {
    super();
    this.swiper = null;
  }
  connectedCallback() {
    // 避免多次初始化
    if (this.swiper) return;

    this.swiper = this.initSwiper(this);
  }
}
if (!customElements.get('yg-swiper')) {
  customElements.define('yg-swiper', YGSwiper);
}
